import {
  Button,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { useEffect, useState } from 'react';
import { fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { EmptyState, InfoCard } from '@backstage/core-components';
import {
  EntityRefLink,
  useEntity,
  useRelatedEntities,
} from '@backstage/plugin-catalog-react';
import {
  Entity,
  RELATION_HAS_PART,
  stringifyEntityRef,
} from '@backstage/catalog-model';

export const CHECKMARX_SOURCE_LOCATION_ANNOTATION = 'amfs.io/source-location';

export const isCheckmarxAvailable = (entity: Entity): boolean =>
  Boolean(entity.metadata.annotations?.[CHECKMARX_SOURCE_LOCATION_ANNOTATION]);

type SeverityCounter = {
  severity?: string;
  counter?: number;
};

type EngineCounters = {
  totalCounter?: number;
  severityCounters?: SeverityCounter[];
};

type ScanSummary = {
  scanId?: string;
  sastCounters?: EngineCounters;
  scaCounters?: EngineCounters;
  kicsCounters?: EngineCounters;
  apisecCounters?: EngineCounters;
};

type CheckmarxSummaryResponse = {
  projectId: string;
  projectName?: string;
  repoUrl?: string;
  branch?: string;
  scanId: string;
  status?: string;
  createdAt?: string;
  link?: string;
  summary?: ScanSummary;
};

function getSeverityCount(
  counters: EngineCounters | undefined,
  severity: string,
): number {
  const match = counters?.severityCounters?.find(
    item => item.severity?.toUpperCase() === severity,
  );
  return match?.counter ?? 0;
}

function extractRepoName(url: string): string {
  return url.replace(/\/+$/, "").split("/").pop() || "";
}

function useCheckmarxSummary(sourceLocation: string | undefined) {
  const fetchApi = useApi(fetchApiRef);
  const [summary, setSummary] = useState<CheckmarxSummaryResponse>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!sourceLocation) {
      setSummary(undefined);
      setError(undefined);
      setIsLoading(false);
      return;
    }

    let isActive = true;

    const loadSummary = async () => {
      setIsLoading(true);
      setError(undefined);

      try {
        const searchParams = new URLSearchParams({ sourceLocation });
        const response = await fetchApi.fetch(
          `/api/checkmarx/summary?${searchParams.toString()}`,
        );

        if (!response.ok) {
          const body = await response.json().catch(() => undefined);
          throw new Error(
            body?.error ??
              `Failed to load Checkmarx scan summary: ${response.status} ${response.statusText}`,
          );
        }

        const result = (await response.json()) as CheckmarxSummaryResponse;

        if (!isActive) {
          return;
        }

        setSummary(result);
      } catch (loadError) {
        if (!isActive) {
          return;
        }

        setError(
          loadError instanceof Error
            ? loadError.message
            : 'Failed to load Checkmarx scan summary.',
        );
        setSummary(undefined);
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadSummary();

    return () => {
      isActive = false;
    };
  }, [fetchApi, sourceLocation]);

  return { summary, isLoading, error };
}

const SEVERITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'];

const ENGINES: Array<{
  label: string;
  key: 'sastCounters' | 'scaCounters' | 'kicsCounters' | 'apisecCounters';
}> = [
  { label: 'SAST', key: 'sastCounters' },
  { label: 'SCA', key: 'scaCounters' },
  { label: 'IaC Security (KICS)', key: 'kicsCounters' },
  { label: 'API Security', key: 'apisecCounters' },
];

const SeverityTable = ({ summary }: { summary?: ScanSummary }) => {
  const engines = ENGINES.map(engine => ({
    label: engine.label,
    counters: summary?.[engine.key],
  })).filter(engine => engine.counters);

  if (!engines.length) {
    return (
      <Typography variant="body2">
        No scan summary counters are available for the latest scan.
      </Typography>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Engine</TableCell>
          {SEVERITIES.map(severity => (
            <TableCell key={severity}>
              {severity.charAt(0) + severity.slice(1).toLowerCase()}
            </TableCell>
          ))}
          <TableCell>Total</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {engines.map(engine => (
          <TableRow key={engine.label}>
            <TableCell>{engine.label}</TableCell>
            {SEVERITIES.map(severity => (
              <TableCell key={severity}>
                {getSeverityCount(engine.counters, severity)}
              </TableCell>
            ))}
            <TableCell>{engine.counters?.totalCounter ?? '-'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const CheckmarxSummaryCard = ({ entity }: { entity: Entity }) => {
  const sourceLocation =
    entity.metadata.annotations?.[CHECKMARX_SOURCE_LOCATION_ANNOTATION];
  const convertedSourceLocation = extractRepoName(sourceLocation)
  const { summary, isLoading, error } = useCheckmarxSummary(convertedSourceLocation);

  if (isLoading) {
    return (
      <InfoCard title="Checkmarx One">
        <Typography variant="body2">
          Loading Checkmarx scan results...
        </Typography>
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="Checkmarx One">
        <Typography color="error">{error}</Typography>
      </InfoCard>
    );
  }

  if (!summary) {
    return (
      <InfoCard title="Checkmarx One">
        <Typography variant="body2">
          No Checkmarx scan results are available for this entity.
        </Typography>
      </InfoCard>
    );
  }

  return (
    <InfoCard title="Checkmarx One">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            Project {summary.projectName ?? summary.projectId}
            {summary.branch ? ` · branch ${summary.branch}` : ''}
            {summary.createdAt
              ? ` · scanned at ${new Date(summary.createdAt).toLocaleString()}`
              : ''}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <SeverityTable summary={summary.summary} />
        </Grid>

        <Grid item xs={12}>
          {summary.link ? (
            <Button
              variant="outlined"
              color="primary"
              href={summary.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Scan in Checkmarx One
            </Button>
          ) : null}
        </Grid>
      </Grid>
    </InfoCard>
  );
};

export const CheckmarxSecurityContent = () => {
  const { entity } = useEntity();
  return <CheckmarxSummaryCard entity={entity} />;
};

const CheckmarxComponentRow = ({ component }: { component: Entity }) => {
  const sourceLocation =
    component.metadata.annotations?.[CHECKMARX_SOURCE_LOCATION_ANNOTATION];
  const convertedSourceLocation = extractRepoName(sourceLocation)
  const { summary, isLoading, error } = useCheckmarxSummary(convertedSourceLocation);

  let criticalCount: number | undefined;
  let highCount: number | undefined;
  let totalCount: number | undefined;

  if (summary?.summary) {
    const counters = [
      summary.summary.sastCounters,
      summary.summary.scaCounters,
      summary.summary.kicsCounters,
      summary.summary.apisecCounters,
    ];
    criticalCount = counters.reduce(
      (total, item) => total + getSeverityCount(item, 'CRITICAL'),
      0,
    );
    highCount = counters.reduce(
      (total, item) => total + getSeverityCount(item, 'HIGH'),
      0,
    );
    totalCount = counters.reduce(
      (total, item) => total + (item?.totalCounter ?? 0),
      0,
    );
  }

  return (
    <TableRow>
      <TableCell>
        <EntityRefLink entityRef={component} />
      </TableCell>
      <TableCell>
        {sourceLocation ? (
          <Link href={sourceLocation} target="_blank" rel="noopener noreferrer">
            Repository
          </Link>
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell>{summary?.branch ?? '-'}</TableCell>
      {isLoading ? (
        <TableCell colSpan={3}>Loading...</TableCell>
      ) : error ? (
        <TableCell colSpan={3}>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        </TableCell>
      ) : (
        <>
          <TableCell>{criticalCount ?? '-'}</TableCell>
          <TableCell>{highCount ?? '-'}</TableCell>
          <TableCell>{totalCount ?? '-'}</TableCell>
        </>
      )}
      <TableCell>
        {summary?.link ? (
          <Link href={summary.link} target="_blank" rel="noopener noreferrer">
            Open in Checkmarx One
          </Link>
        ) : (
          '-'
        )}
      </TableCell>
    </TableRow>
  );
};

export const CheckmarxSystemOverview = () => {
  const { entity } = useEntity();
  const { entities, loading, error } = useRelatedEntities(entity, {
    type: RELATION_HAS_PART,
    kind: 'Component',
  });

  if (loading) {
    return (
      <InfoCard title="Checkmarx One">
        <Typography variant="body2">Loading related components...</Typography>
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="Checkmarx One">
        <Typography color="error">{error.message}</Typography>
      </InfoCard>
    );
  }

  const components = (entities ?? []).filter(isCheckmarxAvailable);

  if (!components.length) {
    return (
      <EmptyState
        title="No Checkmarx scan results available for this system"
        missing="info"
        description={`None of the related components define the ${CHECKMARX_SOURCE_LOCATION_ANNOTATION} annotation.`}
      />
    );
  }

  return (
    <InfoCard title="Checkmarx One">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Component</TableCell>
            <TableCell>Repository</TableCell>
            <TableCell>Branch</TableCell>
            <TableCell>Critical</TableCell>
            <TableCell>High</TableCell>
            <TableCell>Total</TableCell>
            <TableCell>Report</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {components.map(component => (
            <CheckmarxComponentRow
              key={stringifyEntityRef(component)}
              component={component}
            />
          ))}
        </TableBody>
      </Table>
    </InfoCard>
  );
};
